import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import View from './view';
import DocumentUtils from 'services/DocumentUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import PerformanceUtils from 'services/PerformanceUtils';

/**
 * @param {any} props
 */
const EditDocument = (props) => {
  const [url, setUrl] = useState();
  const [pages, setPages] = useState([]);
  const [lastClicked, setLastClicked] = useState();
  const [selected, setSelected] = useState([]);
  const [stack, setStack] = useState([]);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDocument, setLoadingDocument] = useState(true);
  const [defaultRotations, setDefaultRotations] = useState([]);
  const [overridedPatient, setOverridedPatient] = useState(undefined);

  const { document } = props;

  const dispatch = useAppDispatch();

  const documentId = document.id;

  useEffect(() => {
    function getInfo() {
      return dispatch(api.Documents.getDocumentFileInfo({ documentId }));
    }

    function getFile(dispatchOptions) {
      return dispatch(api.Documents.getDocumentFile({ documentId }), dispatchOptions);
    }

    if (!props.url) {
      DocumentUtils.getFileUrl(getFile, getInfo).then(({ fileUrl }) => {
        setUrl(fileUrl);
      });
    } else {
      setUrl(props.url);
    }
  }, [documentId, dispatch, props.url]);

  function handleSelectionClear() {
    setLastClicked();
    setSelected([]);
  }

  function handleSelectionFinish(elements) {
    if (elements.length > 0) setLastClicked(elements[elements.length - 1].props.page);
    else setLastClicked(undefined);
    setSelected(elements.map((element) => element.props.page));
  }

  function addStackItem(action, value, pages = selected) {
    let stackItem = { action, pages, document };

    let clearSelection = true;

    if (action === 'add' && value) {
      stackItem.document = value;
      delete stackItem.document.id;
      delete stackItem.document.filename;
    }

    if (action === 'rotation') {
      clearSelection = false;
      let newStack = (stack || []).filter((item) => item.action !== 'rotation');

      if (Object.keys(value).length === 0) {
        setStack(newStack);
        return;
      }

      delete stackItem.document;
      stackItem.rotations = [...value];
      stackItem.pages = value.map((val) => {
        return { number: val.page };
      });
    }

    setStack([...stack, stackItem]);
    if (clearSelection) {
      setSelected([]);
      setLastClicked();
      setShowDetailsPanel(false);
    }
  }

  function addRotation(page, value) {
    let found = getRotationAction();
    let rotations = found.rotations || [];

    if (rotations.filter((rotation) => rotation.page === page).length === 0)
      rotations.push({ page, degrees: 0 });

    rotations = rotations
      .map((rotation) => {
        if (rotation.page === page) rotation.degrees = (rotation.degrees + value) % 360;

        return rotation.degrees === 0 ? undefined : rotation;
      })
      .filter((rotation) => rotation !== undefined);

    addStackItem('rotation', rotations);
  }

  function getRotationAction() {
    return stack.filter((stackItem) => stackItem.action === 'rotation')[0] || {};
  }

  const pagesWithActions = useMemo(() => {
    let result = [];
    stack.forEach((s) => {
      if (s.action === 'delete' || s.action === 'add') {
        result = [...result, ...s.pages.map((p) => p.number)];
      }
    });

    return _.uniq(result);
  }, [stack]);

  function onPageLoadSuccess({ _pageInfo, pageIndex }) {
    pageIndex++;
    let defaultRot = [...defaultRotations];
    defaultRot[pageIndex] = _pageInfo.rotate || 0;
    setDefaultRotations(defaultRot);
  }

  function onDocumentLoadSuccess({ numPages }) {
    let pages = [];
    for (let i = 0; i < numPages; i++) {
      pages.push({ number: i + 1 });
    }
    setPages(pages);
    setLoadingDocument(false);
  }

  function deleteStackItem(toRemoveItem) {
    let newStack = (stack || []).filter((item) => {
      return item !== toRemoveItem;
    });
    setStack(newStack);
  }

  function deletePage(pageNumber) {
    addStackItem('delete', undefined, [pageNumber]);
  }

  async function saveChanges() {
    let data = stack.map((action) => {
      let newAction = PerformanceUtils.clone(action);
      if (newAction.action === 'rotation') {
        newAction.rotations = newAction.rotations.map((rotation) => {
          return {
            page: rotation.page,
            degrees: ((defaultRotations[rotation.page] || 0) + rotation.degrees) % 360,
          };
        });
      }
      return {
        ...newAction,
        pages: newAction.pages.map((page) => page.number),
      };
    });
    setLoading(true);
    const result = await dispatch(
      api.Documents.editDocument({ documentId: document.id, documentEditActions: data }),
      {
        title: 'Edit Document',
        successText: 'Actions applied successfully',
      },
    );
    setLoading(false);
    if (result) {
      setStack([]);
      const finalResult = result.find((doc) => doc.id === document.id);
      if (props.onClose) props.onClose(finalResult);
    }
  }

  function showPanel(panel, value = true) {
    switch (panel) {
      case 'details':
        setShowDetailsPanel(value);
        break;
      default:
        break;
    }
  }

  async function onSelectPatient(patient) {
    if (patient) {
      setOverridedPatient(patient);
      await new Promise((resolve) => setTimeout(resolve, 100));
      setOverridedPatient(undefined);
    }
  }

  let isPanelOpen = showDetailsPanel ? 'details' : false;

  let rotation = getRotationAction().rotations || [];

  return (
    <View
      {...props}
      url={url}
      pages={pages}
      lastClicked={lastClicked}
      selected={selected}
      stack={stack}
      loading={loading}
      loadingDocument={loadingDocument}
      handleSelectionFinish={handleSelectionFinish}
      addStackItem={addStackItem}
      saveChanges={saveChanges}
      deleteStackItem={deleteStackItem}
      handleSelectionClear={handleSelectionClear}
      onDocumentLoadSuccess={onDocumentLoadSuccess}
      showPanel={showPanel}
      isPanelOpen={isPanelOpen}
      rotation={rotation}
      addRotation={addRotation}
      onPageLoadSuccess={onPageLoadSuccess}
      defaultRotations={defaultRotations}
      pagesWithActions={pagesWithActions}
      overridedPatient={overridedPatient}
      onSelectPatient={onSelectPatient}
      deletePage={deletePage}
    />
  );
};

EditDocument.propTypes = {
  document: PropTypes.object.isRequired,
  onClose: PropTypes.func,
};

export default EditDocument;
